import { t } from "i18next";
import * as z from "zod";

import {
  DISCOUNT_TYPES,
  PROMOTION_STATUSES,
} from "@/features/shared/api/enums";

/**
 * Schéma du formulaire de création d'une promotion
 * (`POST /Promotions/add`, guide §6.5).
 *
 * `value` — sens dépend de `discountType` :
 *  - PERCENT_OFF_FEE : pourcentage (0-100)
 *  - FIXED_OFF_FEE : montant en unité MAJEURE (converti en mineure au submit)
 *  - FEE_WAIVED : ignoré côté serveur (frais mis à 0)
 *
 * Factory (règle projet) : créé via `createPromotionSchema()` et mémoïsé
 * au niveau composant sur la langue courante — jamais d'instance exportée.
 */
export const createPromotionSchema = () =>
  z.object({
    code: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") })
      .max(64, { message: t("invalidFieldError") })
      .regex(/^[A-Z0-9_-]+$/, { message: t("invalidFieldError") }),
    description: z.string().trim().or(z.literal("")).optional(),
    discountType: z.enum(DISCOUNT_TYPES, {
      errorMap: () => ({ message: t("requiredFieldError") }),
    }),
    value: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .min(0, { message: t("invalidFieldError") }),
    productCode: z.string().trim().or(z.literal("")).optional(),
    usageLimitTotal: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .int({ message: t("invalidFieldError") })
      .min(0, { message: t("invalidFieldError") })
      .optional(),
    usageLimitPerParty: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .int({ message: t("invalidFieldError") })
      .min(0, { message: t("invalidFieldError") })
      .optional(),
    validFrom: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
    validTo: z.string().trim().or(z.literal("")).optional(),
    status: z.enum(PROMOTION_STATUSES, {
      errorMap: () => ({ message: t("requiredFieldError") }),
    }),
  });

export type PromotionFormValues = z.infer<
  ReturnType<typeof createPromotionSchema>
>;
